import {
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  ViewDay as ViewDayIcon,
  ViewModule as ViewModuleIcon,
  ViewWeek as ViewWeekIcon,
  Repeat as RepeatIcon,
  PersonAdd as PersonAddIcon,
} from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, IconButton, Paper, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import moment from "moment";
import "moment-timezone";
import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer, View, Formats } from "react-big-calendar";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { tutorials } from "../config/tutorials";
import { useOAuthHealth } from "../hooks/useOAuthHealth";
import { usePageTutorial } from "../hooks/useTutorial";
import { CalendarProvider, calendarService } from "../services/CalendarService";
import { CalendarEvent } from "../types/calendar";
import { EventEditModal } from "./EventEditModal";
import { EventDetailsPopup } from "./EventDetailsPopup";
import { HelpIcon } from "./HelpIcon";
import { RecurringEventScopeModal } from "./RecurringEventScopeModal";
import { TimezoneSelector } from "./TimezoneSelector";
import { userProfileService } from "../services/UserProfileService";
import { TimezoneUtils } from "../utils/timezoneUtils";

import { getClassificationColor } from "../config/classification";
import { logger } from "../utils/logger";

// We'll create the localizer dynamically based on the selected timezone

interface CalendarEventWithUI extends CalendarEvent {
  userId: string;
  startTimeUTC: Date;
  endTimeUTC: Date;
  classification?: string;
  color: string;
  isBlocked: boolean;
  isRecurring?: boolean;
  isInstance?: boolean;
  baseEventId?: string;
  isException?: boolean;
}

// Interface for React Big Calendar events
interface BigCalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay: boolean;
  resource?: any;
  classification?: string;
  color: string;
  location?: string;
  description?: string;
  isBlocked: boolean;
  isRecurring?: boolean;
  isInstance?: boolean;
  baseEventId?: string;
  isException?: boolean;
  editable?: boolean;
  external?: boolean;
  isInvited?: boolean;
  userResponseStatus?: string;
}

// Create the DnD Calendar component
const DnDCalendar = withDragAndDrop<BigCalendarEvent>(Calendar);

export const CalendarPage: React.FC = () => {
  const [events, setEvents] = useState<CalendarEventWithUI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentProvider, setCurrentProvider] = useState<CalendarProvider | null>(null);
  const [calendarView, setCalendarView] = useState<View>("week");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<import("../services/CalendarService").CalendarEvent | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [calendarDisplayTimezone, setCalendarDisplayTimezone] = useState<string>("");
  const [selectedSlot, setSelectedSlot] = useState<{ start: Date; end: Date } | null>(null);
  const [selectedEventForDetails, setSelectedEventForDetails] = useState<CalendarEventWithUI | null>(null);
  const calendarRef = React.useRef<any>(null);

  // Recurring event scope modal state
  const [scopeModalOpen, setScopeModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: "move" | "resize" | "delete" | "edit";
    event: CalendarEventWithUI;
    newStart?: Date;
    newEnd?: Date;
    eventData?: any;
  } | null>(null);

  // Hide time prefix in week view events
  const weekFormats = React.useMemo(
    () => ({
      eventTimeRangeFormat: () => "",
    }),
    []
  );

  // // Create dynamic localizer based on selected timezone
  // const localizer = React.useMemo(() => {
  //   if (calendarDisplayTimezone) {
  //     // Set the default timezone for moment globally
  //     moment.tz.setDefault(calendarDisplayTimezone);
  //     console.log('📅 Set moment default timezone to:', calendarDisplayTimezone);
  //     return momentLocalizer(moment);
  //   }
  //   // Reset to local timezone when no timezone is selected
  //   moment.tz.setDefault();
  //   console.log('📅 Reset moment to local timezone');
  //   return momentLocalizer(moment);
  // }, [calendarDisplayTimezone]);

  const localizer = momentLocalizer(moment);

  // Initialize tutorial for this page
  usePageTutorial("calendar_page_tour", tutorials.calendar);

  // OAuth health check
  const { isPluginHealthy } = useOAuthHealth();
  const [isGoogleHealthy, setIsGoogleHealthy] = useState<boolean | null>(null);

  // Check OAuth health on mount and when needed
  useEffect(() => {
    const checkOAuthHealth = async () => {
      try {
        console.log("🔍 CalendarPage: Checking Google OAuth health...");
        const healthy = await isPluginHealthy("GooglePlugin");
        console.log("🔍 CalendarPage: Google OAuth health result:", healthy);
        setIsGoogleHealthy(healthy);
      } catch (error) {
        console.error("🔍 CalendarPage: Failed to check OAuth health:", error);
        setIsGoogleHealthy(false);
      }
    };

    checkOAuthHealth();
  }, [isPluginHealthy]);

  // Load user timezone and set calendar display timezone
  useEffect(() => {
    const loadUserTimezone = async () => {
      try {
        const profile = await userProfileService.getUserProfile();
        if (profile?.timezone) {
          setCalendarDisplayTimezone(profile.timezone); // Default calendar display to user's timezone
        } else {
          // Fallback to browser timezone
          const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          setCalendarDisplayTimezone(browserTimezone);
        }
      } catch (error) {
        console.error("Error loading user timezone:", error);
        // Fallback to browser timezone
        const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setCalendarDisplayTimezone(browserTimezone);
      }
    };

    loadUserTimezone();
  }, []);

  // Convert CalendarEventWithUI to BigCalendarEvent
  const convertToBigCalendarEvents = (events: CalendarEventWithUI[]): BigCalendarEvent[] => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    console.log("📅 Converting events to BigCalendar format:", {
      eventCount: events.length,
      events: events.map((e) => ({
        id: e.id,
        title: e.title,
        startTime: e.startTime,
        endTime: e.endTime,
        allDay: e.allDay,
        timezone: e.timezone,
      })),
    });

    const bigCalendarEvents = events
      .map((event) => {
        // Ensure we have valid Date objects for React Big Calendar
        let startDate: Date;
        let endDate: Date;

        try {
          // Convert startTime to valid Date
          if (event.startTime instanceof Date) {
            startDate = event.startTime;
          } else if (typeof event.startTime === "string") {
            startDate = new Date((event.startTime as string).replace("Z", ""));
          } else {
            startDate = new Date((event.startTime as string).replace("Z", ""));
          }

          // Convert endTime to valid Date
          if (event.endTime instanceof Date) {
            endDate = event.endTime;
          } else if (typeof event.endTime === "string") {
            endDate = new Date((event.endTime as string).replace("Z", ""));
          } else {
            endDate = new Date((event.endTime as string).replace("Z", ""));
          }

          // Validate dates
          if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
            console.warn(`📅 CalendarPage: Invalid dates for event ${event.title}, skipping`);
            return null;
          }

          // Convert event times from their stored timezone to the display timezone
          let displayStartDate = startDate;
          let displayEndDate = endDate;

          if (calendarDisplayTimezone && event.timezone && event.timezone !== calendarDisplayTimezone) {
            // Convert from event timezone to display timezone
            displayStartDate = TimezoneUtils.convertDate(startDate, event.timezone, calendarDisplayTimezone);
            displayEndDate = TimezoneUtils.convertDate(endDate, event.timezone, calendarDisplayTimezone);
          }

          // const bcConvertedLocalStartDate = TimezoneUtils.convertDate(displayStartDate, browserTimezone, 'UTC');
          // const bcConvertedLocalEndDate = TimezoneUtils.convertDate(displayEndDate, browserTimezone, 'UTC');

          // console.log('🔄 Event:', event.title, 'displayStartDate:', displayStartDate, 'displayEndDate:', displayEndDate);
          // console.log('🔄 Event:', event.title, 'startDate:', startDate, 'endDate:', endDate);
          // console.log('🔄 Event:', event.title, 'startTimeUTC:', event.startTimeUTC, 'endTimeUTC:', event.endTimeUTC);
          // console.log('🔄 Event:', event.title, 'startTime:', event.startTime, 'endTime:', event.endTime);
          // console.log('🔄 Event:', event.title, 'timezone:', event.timezone);
          // console.log('🔄 Event:', event.title, 'calendarDisplayTimezone:', calendarDisplayTimezone);

          const bigCalendarEvent = {
            id: event.id,
            title: event.title,
            // start: bcConvertedLocalStartDate,
            // end: bcConvertedLocalEndDate,
            start: displayStartDate,
            end: displayEndDate,
            allDay: event.allDay || false,
            classification: (event as any).classification,
            color: event.color,
            location: event.location,
            description: event.description,
            isBlocked: event.isBlocked,
            isRecurring: !!(event as any).recurrence || !!(event as any).baseEventId,
            isInstance: !!(event as any).baseEventId,
            baseEventId: (event as any).baseEventId,
            isException: (event as any).isException || false,
            external: event.external || false,
            isInvited: event.isInvited || false,
            userResponseStatus: event.userResponseStatus,
            resource: event,
          };

          // Debug logging for event conversion
          // console.log('📅 Converted event for BigCalendar:', {
          //   originalEvent: {
          //     id: event.id,
          //     title: event.title,
          //     startTime: event.startTime,
          //     endTime: event.endTime,
          //     allDay: event.allDay,
          //     timezone: event.timezone
          //   },
          //   bigCalendarEvent: {
          //     id: bigCalendarEvent.id,
          //     title: bigCalendarEvent.title,
          //     start: bigCalendarEvent.start,
          //     end: bigCalendarEvent.end,
          //     allDay: bigCalendarEvent.allDay,
          //     hasResource: !!bigCalendarEvent.resource
          //   },
          //   hasStart: !!bigCalendarEvent.start,
          //   hasEnd: !!bigCalendarEvent.end,
          //   startType: typeof bigCalendarEvent.start,
          //   endType: typeof bigCalendarEvent.end,
          //   startValid: bigCalendarEvent.start instanceof Date && !isNaN(bigCalendarEvent.start.getTime()),
          //   endValid: bigCalendarEvent.end instanceof Date && !isNaN(bigCalendarEvent.end.getTime())
          // });

          // Safety check to ensure the event has the required properties
          if (!bigCalendarEvent.start || !bigCalendarEvent.end) {
            console.error("📅 CalendarPage: Event missing required start/end properties:", bigCalendarEvent);
            return null;
          }

          if (!(bigCalendarEvent.start instanceof Date) || !(bigCalendarEvent.end instanceof Date)) {
            console.error("📅 CalendarPage: Event start/end are not Date objects:", bigCalendarEvent);
            return null;
          }

          if (isNaN(bigCalendarEvent.start.getTime()) || isNaN(bigCalendarEvent.end.getTime())) {
            console.error("📅 CalendarPage: Event has invalid dates:", bigCalendarEvent);
            return null;
          }

          return bigCalendarEvent;
        } catch (error) {
          console.error(`📅 CalendarPage: Error converting event ${event.title}:`, error);
          return null;
        }
      })
      .filter((event) => event !== null) as BigCalendarEvent[];

    console.log("📅 Final BigCalendar events:", {
      count: bigCalendarEvents.length,
      events: bigCalendarEvents.map((e) => ({
        id: e.id,
        title: e.title,
        start: e.start,
        end: e.end,
        allDay: e.allDay,
        hasResource: !!e.resource,
        resourceKeys: e.resource ? Object.keys(e.resource) : "no resource",
      })),
    });

    console.log(`📅 CalendarPage: Converted ${bigCalendarEvents.length} events for React Big Calendar`);
    const pastEvents = bigCalendarEvents.filter((e) => e.start < new Date());
    const currentEvents = bigCalendarEvents.filter((e) => e.start >= new Date());
    console.log(`📅 CalendarPage: BigCalendar events - ${pastEvents.length} past, ${currentEvents.length} current/future`);

    return bigCalendarEvents;
  };

  // Navigation handler with date range restrictions
  const handleNavigate = (newDate: Date) => {
    const now = new Date();
    const oneMonthAgo = new Date(now);
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

    const sixMonthsFromNow = new Date(now);
    sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

    // Restrict navigation to allowed range
    if (newDate < oneMonthAgo) {
      logger.warn("📅 CalendarPage: Navigation restricted - cannot go beyond 1 month back");
      return;
    }

    if (newDate > sixMonthsFromNow) {
      logger.warn("📅 CalendarPage: Navigation restricted - cannot go beyond 6 months forward");
      return;
    }

    fetchEvents(newDate);
  };

  // Event handlers for React Big Calendar
  const handleSelectEvent = (event: BigCalendarEvent) => {
    console.log("🎯 Event selected:", event.title, event.id);
    const originalEvent = event.resource as CalendarEventWithUI;

    // Log recurring event information
    if (event.isRecurring || event.isInstance) {
      console.log("🔄 Recurring event selected:", {
        isRecurring: event.isRecurring,
        isInstance: event.isInstance,
        baseEventId: event.baseEventId,
        isException: event.isException,
      });
    }

    // Show event details popup
    setSelectedEventForDetails(originalEvent);
  };

  // Handle delete event from calendar interactions
  const handleDeleteEventFromCalendar = (event: CalendarEventWithUI) => {
    if (event.isRecurring && event.isInstance && !event.isException) {
      // Show scope modal for recurring event instances
      setPendingAction({
        type: "delete",
        event: event,
      });
      setScopeModalOpen(true);
    } else {
      // Regular event or base recurring event
      if (window.confirm(`Are you sure you want to delete "${event.title}"?`)) {
        calendarService
          .deleteEvent(event.id)
          .then(() => {
            console.log("✅ Event deleted successfully:", event.title);
            fetchEvents();
          })
          .catch((error) => {
            console.error("❌ Failed to delete event:", error);
          });
      }
    }
  };

  // Handle keyboard events for delete operations
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Delete" || event.key === "Backspace") {
      if (editingEvent) {
        event.preventDefault();
        handleDeleteEventFromCalendar(editingEvent as CalendarEventWithUI);
      }
    }
  };

  const handleSelectSlot = ({ start, end }: { start: Date; end: Date }) => {
    // Only create new event if we're not currently dragging
    if (!isDragging) {
      console.log("📅 Slot selected:", start.toLocaleString(), "to", end.toLocaleString());
      console.log("📅 Slot selected (ISO):", start.toISOString(), "to", end.toISOString());
      console.log("📅 Current calendar timezone:", calendarDisplayTimezone);
      console.log("📅 Moment default timezone:", moment.tz.guess());

      // Pass selected slot directly; RBC provides absolute instants
      setEditingEvent(null);
      setSelectedSlot({ start, end });
      setIsCreating(true);
      setEditModalOpen(true);
    } else {
      console.log("🚫 Slot selection ignored - currently dragging");
    }
  };

  // Handle event details popup callbacks
  const handleEventDetailsUpdate = (updatedEvent: CalendarEvent) => {
    console.log("🔄 Event updated from details popup:", updatedEvent.title);

    // Update the event in the local state
    setEvents((prevEvents) => prevEvents.map((e) => (e.id === updatedEvent.id ? convertToCalendarEventWithUI(updatedEvent as any) : e)));

    // Update the selected event for details popup if it's the same event
    if (selectedEventForDetails && selectedEventForDetails.id === updatedEvent.id) {
      setSelectedEventForDetails(convertToCalendarEventWithUI(updatedEvent as any));
    }

    // Don't close the popup - let the user see the updated state
    // setSelectedEventForDetails(null);
  };

  const handleEventDetailsDelete = (eventId: string) => {
    console.log("🗑️ Event deleted from details popup:", eventId);
    fetchEvents(); // Refresh the events list
    setSelectedEventForDetails(null); // Close details popup
  };

  // Convert CalendarEventWithUI to CalendarEvent from types/calendar
  const convertToTypesCalendarEvent = (event: CalendarEventWithUI): CalendarEvent => {
    return {
      id: event.id,
      title: event.title,
      description: event.description,
      startTime: event.startTime,
      endTime: event.endTime,
      allDay: event.allDay,
      location: event.location,
      meetingLink: event.meetingLink,
      timezone: event.timezone,
      attendees: event.attendees,
      organizer: event.organizer,
      status: event.status,
      visibility: event.visibility || "default",
      recurrence: event.recurrence,
      reminders: event.reminders,
      color: event.color,
      classification: event.classification,
      providerId: event.providerId,
      providerEventId: event.providerEventId,
      syncStatus: event.syncStatus,
      lastSyncedAt: event.lastSyncedAt,
      lastModifiedAt: event.lastModifiedAt,
      metadata: event.metadata,
      createdAt: event.createdAt,
      updatedAt: event.updatedAt,
      userEmail: event.userEmail,
      isInvited: event.isInvited,
      userResponseStatus: event.userResponseStatus,
      organizerEmail: event.organizerEmail,
      editable: event.editable,
      external: event.external,
    };
  };

  // Convert CalendarEvent from types/calendar back to CalendarEvent from services/CalendarService
  const convertToServiceCalendarEvent = (event: CalendarEvent): import("../services/CalendarService").CalendarEvent => {
    return {
      id: event.id,
      userId: "", // This will be set by the service
      title: event.title,
      description: event.description,
      startTime: event.startTime,
      endTime: event.endTime,
      startTimeUTC: event.startTime, // Use startTime as UTC fallback
      endTimeUTC: event.endTime, // Use endTime as UTC fallback
      allDay: event.allDay,
      location: event.location,
      meetingLink: event.meetingLink,
      timezone: event.timezone,
      attendees: event.attendees?.map((attendee) => ({
        email: attendee.email,
        displayName: attendee.displayName || attendee.email,
        responseStatus: attendee.responseStatus as "accepted" | "declined" | "tentative" | "needsAction",
      })),
      status: event.status,
      color: event.color,
      classification: event.classification,
      providerId: event.providerId,
      providerEventId: event.providerEventId,
      syncStatus: event.syncStatus,
      lastSyncedAt: event.lastSyncedAt,
      lastModifiedAt: event.lastModifiedAt,
      createdAt: event.createdAt,
      updatedAt: event.updatedAt,
      recurrence: event.recurrence,
      isBaseEvent: false,
      baseEventId: undefined,
      originalStartTime: undefined,
      sequence: undefined,
      rrule: undefined,
      visibility: event.visibility,
      isException: undefined,
      userEmail: event.userEmail,
      isInvited: event.isInvited,
      userResponseStatus: event.userResponseStatus,
      organizerEmail: event.organizerEmail,
      editable: event.editable,
      external: event.external,
    };
  };

  const handleEventDetailsEdit = (event: CalendarEvent) => {
    console.log("✏️ Edit event from details popup:", event.title);

    // Prevent editing external events
    if (event.external === true) {
      console.log("❌ Cannot edit external event:", event.title);
      alert("This event cannot be edited as it is organized by someone else");
      return;
    }

    setEditingEvent(convertToServiceCalendarEvent(event));
    setEditModalOpen(true);
    setSelectedEventForDetails(null); // Close details popup
  };

  // Handle scope selection for edit events
  const handleEditScopeSelection = (scope: "instance" | "future" | "series", eventData: any) => {
    if (!editingEvent) return;

    // Convert CalendarEvent to CalendarEventWithUI for pendingAction
    const eventWithUI = convertToCalendarEventWithUI(editingEvent) as CalendarEventWithUI;

    // Set up pending action for scope modal
    setPendingAction({
      type: "edit",
      event: eventWithUI,
      eventData: eventData,
    });
    setScopeModalOpen(true);
  };

  // Handle scope confirmation for edit events
  const handleEditScopeConfirm = async (scope: "instance" | "future" | "series") => {
    if (!pendingAction || pendingAction.type !== "edit") return;

    const { event, eventData } = pendingAction;
    setScopeModalOpen(false);
    setPendingAction(null);

    try {
      let updatedEvent: CalendarEvent;

      if (scope === "instance") {
        updatedEvent = await calendarService.updateEventInstance(event.id, eventData);
      } else if (scope === "future") {
        const result = await calendarService.updateEventFutureInstances(event.id, eventData);
        updatedEvent = result.newBaseEvent;
      } else if (scope === "series" && event.baseEventId) {
        updatedEvent = await calendarService.updateEventSeries(event.baseEventId, eventData);
      } else {
        // Fallback to regular update
        updatedEvent = await calendarService.updateEvent(event.id, eventData);
      }

      // Update the event in the list
      setEvents((prevEvents) => prevEvents.map((e) => (e.id === updatedEvent.id ? { ...e, ...updatedEvent } : e)));

      // Close edit modal
      setEditModalOpen(false);
      setEditingEvent(null);
    } catch (error) {
      console.error("Error updating event:", error);
      alert(error instanceof Error ? error.message : "Failed to update event");
    }
  };

  // Close event details popup
  const handleCloseEventDetails = () => {
    setSelectedEventForDetails(null);
  };

  // Handle recurring event scope selection
  const handleRecurringEventScope = (scope: "instance" | "future" | "series") => {
    if (!pendingAction) return;

    const { type, event, newStart, newEnd, eventData } = pendingAction;
    setScopeModalOpen(false);
    setPendingAction(null);

    const performAction = async () => {
      try {
        if (type === "move" && newStart && newEnd) {
          // For all-day events, adjust the end time to be 00:00 of the next day
          let adjustedEndTime = newEnd;
          if (event.allDay) {
            const adjustedEndDate = new Date(newEnd);
            adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
            adjustedEndDate.setHours(0, 0, 0, 0);
            adjustedEndTime = adjustedEndDate;

            console.log("📏 Recurring all-day event move - adjusted end time:", {
              originalEnd: newEnd,
              adjustedEnd: adjustedEndTime,
              allDay: event.allDay,
            });
          }

          if (scope === "instance") {
            await calendarService.updateEventInstance(event.id, { startTime: newStart, endTime: adjustedEndTime });
          } else if (scope === "future") {
            await calendarService.updateEventFutureInstances(event.id, { startTime: newStart, endTime: adjustedEndTime });
          } else if (scope === "series" && event.baseEventId) {
            await calendarService.updateEventSeries(event.baseEventId, { startTime: newStart, endTime: adjustedEndTime });
          }
        } else if (type === "resize" && newStart && newEnd) {
          // For all-day events, adjust the end time to be 00:00 of the next day
          let adjustedEndTime = newEnd;
          if (event.allDay) {
            const adjustedEndDate = new Date(newEnd);
            adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
            adjustedEndDate.setHours(0, 0, 0, 0);
            adjustedEndTime = adjustedEndDate;

            console.log("📏 Recurring all-day event resize - adjusted end time:", {
              originalEnd: newEnd,
              adjustedEnd: adjustedEndTime,
              allDay: event.allDay,
            });
          }

          if (scope === "instance") {
            await calendarService.updateEventInstance(event.id, { startTime: newStart, endTime: adjustedEndTime });
          } else if (scope === "future") {
            await calendarService.updateEventFutureInstances(event.id, { startTime: newStart, endTime: adjustedEndTime });
          } else if (scope === "series" && event.baseEventId) {
            await calendarService.updateEventSeries(event.baseEventId, { startTime: newStart, endTime: adjustedEndTime });
          }
        } else if (type === "delete") {
          if (scope === "instance") {
            await calendarService.deleteEventInstance(event.id);
          } else if (scope === "future") {
            await calendarService.deleteEventFutureInstances(event.id);
          } else if (scope === "series" && event.baseEventId) {
            await calendarService.deleteEventSeries(event.baseEventId);
          }
        } else if (type === "edit" && eventData) {
          // Handle edit events
          let updatedEvent: CalendarEvent;

          if (scope === "instance") {
            updatedEvent = await calendarService.updateEventInstance(event.id, eventData);
          } else if (scope === "future") {
            const result = await calendarService.updateEventFutureInstances(event.id, eventData);
            updatedEvent = result.newBaseEvent;
          } else if (scope === "series" && event.baseEventId) {
            updatedEvent = await calendarService.updateEventSeries(event.baseEventId, eventData);
          } else {
            // Fallback to regular update
            updatedEvent = await calendarService.updateEvent(event.id, eventData);
          }

          // Update the event in the list
          setEvents((prevEvents) => prevEvents.map((e) => (e.id === updatedEvent.id ? { ...e, ...updatedEvent } : e)));

          // Close edit modal
          setEditModalOpen(false);
          setEditingEvent(null);
          return; // Don't refresh events since we already updated the list
        }

        // Refresh events to show updated state
        fetchEvents();
      } catch (error) {
        console.error(`Failed to ${type} recurring event:`, error);
        // Refresh events to revert UI changes
        fetchEvents();
      } finally {
        setIsMoving(false);
        setIsSaving(false);
      }
    };

    performAction();
  };

  const handleEventDrop = ({ event, start, end }: { event: BigCalendarEvent; start: string | Date; end: string | Date }) => {
    console.log("🔄 Event dropped:", event.title, "from", event.start.toLocaleString(), "to", start.toString());
    console.log("🔄 Event drop object structure:", {
      event: event,
      hasResource: !!event.resource,
      resourceType: typeof event.resource,
      start: event.start,
      end: event.end,
      startType: typeof event.start,
      endType: typeof event.end,
      eventKeys: Object.keys(event),
      resourceKeys: event.resource ? Object.keys(event.resource) : "no resource",
    });

    setIsDragging(false);
    setIsMoving(true);
    const originalEvent = event.resource as CalendarEventWithUI;
    if (originalEvent) {
      // Check if event is editable before allowing move
      if (originalEvent.editable === false) {
        console.log("❌ Cannot move non-editable event:", originalEvent.title);
        setIsMoving(false);
        alert("This event cannot be moved as it is organized by someone else");
        return;
      }

      const startDate = typeof start === "string" ? new Date(start) : start;
      let endDate = typeof end === "string" ? new Date(end) : end;

      // For all-day events, adjust the end time to be 00:00 of the next day
      if (originalEvent.allDay) {
        // For all-day events, the end date should be 00:00 of the next day (exclusive boundary)
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
        adjustedEndDate.setHours(0, 0, 0, 0);
        endDate = adjustedEndDate;

        console.log("🔄 All-day event drop - adjusted end time:", {
          originalEnd: end,
          adjustedEnd: endDate,
          allDay: originalEvent.allDay,
        });
      }

      // Check if this is a recurring event instance
      if (originalEvent.isRecurring && originalEvent.isInstance && !originalEvent.isException) {
        console.log("🔄 Moving recurring event instance:", originalEvent.title);
        // Show scope modal for recurring event instances
        setPendingAction({
          type: "move",
          event: originalEvent,
          newStart: startDate,
          newEnd: endDate,
        });
        setScopeModalOpen(true);
        setIsMoving(false);
      } else {
        // Regular event or base recurring event
        calendarService
          .moveEvent(originalEvent.id, startDate, endDate)
          .then((movedEvent) => {
            console.log("✅ Event moved successfully:", movedEvent.title);
            // Update the event in the list with proper formatting
            setEvents((prevEvents) => prevEvents.map((e) => (e.id === movedEvent.id ? convertToCalendarEventWithUI(movedEvent) : e)));
          })
          .catch((error) => {
            console.error("❌ Failed to move event:", error);
            // Refresh events to revert the UI change
            fetchEvents();
          })
          .finally(() => {
            setIsMoving(false);
          });
      }
    }
  };

  const handleEventResize = ({ event, start, end }: { event: BigCalendarEvent; start: string | Date; end: string | Date }) => {
    console.log("📏 Event resized:", event.title, "from", event.start.toLocaleString() + "-" + event.end.toLocaleString(), "to", start.toString() + "-" + end.toString());
    console.log("📏 Event object structure:", {
      event: event,
      hasResource: !!event.resource,
      resourceType: typeof event.resource,
      start: event.start,
      end: event.end,
      startType: typeof event.start,
      endType: typeof event.end,
      eventKeys: Object.keys(event),
      resourceKeys: event.resource ? Object.keys(event.resource) : "no resource",
    });

    setIsDragging(false);
    setIsSaving(true);
    const originalEvent = event.resource as CalendarEventWithUI;
    if (originalEvent) {
      // Check if event is editable before allowing resize
      if (originalEvent.editable === false) {
        console.log("❌ Cannot resize non-editable event:", originalEvent.title);
        setIsSaving(false);
        alert("This event cannot be resized as it is organized by someone else");
        return;
      }

      const startDate = typeof start === "string" ? new Date(start) : start;
      let endDate = typeof end === "string" ? new Date(end) : end;

      // For all-day events, adjust the end time to be 00:00 of the next day
      if (originalEvent.allDay) {
        // For all-day events, the end date should be 00:00 of the next day (exclusive boundary)
        const adjustedEndDate = new Date(endDate);
        adjustedEndDate.setDate(adjustedEndDate.getDate() + 1);
        adjustedEndDate.setHours(0, 0, 0, 0);
        endDate = adjustedEndDate;

        console.log("📏 All-day event resize - adjusted end time:", {
          originalEnd: end,
          adjustedEnd: endDate,
          allDay: originalEvent.allDay,
        });
      }

      // Check if this is a recurring event instance
      if (originalEvent.isRecurring && originalEvent.isInstance && !originalEvent.isException) {
        console.log("📏 Resizing recurring event instance:", originalEvent.title);
        // Show scope modal for recurring event instances
        setPendingAction({
          type: "resize",
          event: originalEvent,
          newStart: startDate,
          newEnd: endDate,
        });
        setScopeModalOpen(true);
        setIsSaving(false);
      } else {
        // Regular event or base recurring event
        calendarService
          .updateEvent(originalEvent.id, { startTime: startDate, endTime: endDate })
          .then((updatedEvent) => {
            console.log("✅ Event resized successfully:", updatedEvent.title);
            // Update the event in the list with proper formatting
            setEvents((prevEvents) => prevEvents.map((e) => (e.id === updatedEvent.id ? convertToCalendarEventWithUI(updatedEvent) : e)));
          })
          .catch((error) => {
            console.error("❌ Failed to resize event:", error);
            // Refresh events to revert the UI change
            fetchEvents();
          })
          .finally(() => {
            setIsSaving(false);
          });
      }
    }
  };

  // Scroll to current time
  const scrollToCurrentTime = () => {
    console.log("🔄 Attempting to scroll to current time...");
    console.log("📅 Calendar view:", calendarView);
    console.log("🔗 Calendar ref:", calendarRef.current);

    if (calendarRef.current && calendarView === "week") {
      // Get current time in the selected display timezone
      const nowInTZ = calendarDisplayTimezone ? TimezoneUtils.getCurrentTimeInTimezone(calendarDisplayTimezone) : new Date();
      const currentHour = nowInTZ.getHours();
      const currentMinute = nowInTZ.getMinutes();

      // Calculate scroll position (assuming 60px per hour)
      const scrollTop = (currentHour + currentMinute / 60) * 60;
      console.log("⏰ Current time in", calendarDisplayTimezone || "local timezone:", nowInTZ.toLocaleTimeString());
      console.log("📊 Calculated scroll position:", scrollTop);

      // Try to find the calendar element in the DOM
      const calendarElement = document.querySelector(".rbc-calendar");
      if (calendarElement) {
        console.log("✅ Found calendar element in DOM");
        // Try multiple selectors to find the scrollable element
        const timeGrid = calendarElement.querySelector(".rbc-time-content") || calendarElement.querySelector(".rbc-time-view") || calendarElement.querySelector(".rbc-time-gutter") || calendarElement;

        if (timeGrid) {
          console.log("✅ Found scrollable element:", timeGrid);
          console.log("📏 Current scrollTop:", timeGrid.scrollTop);
          timeGrid.scrollTop = scrollTop - 200; // Offset to show current time in the middle
          console.log("📏 New scrollTop:", timeGrid.scrollTop);
        } else {
          console.log("❌ Could not find scrollable element");
        }
      } else {
        console.log("❌ Could not find calendar element in DOM");
      }
    } else {
      console.log("❌ Calendar ref not ready or not in week view");
    }
  };

  // Scroll to current time when component mounts or view changes
  useEffect(() => {
    console.log("🔄 useEffect triggered - checking if should scroll");
    console.log("📅 Calendar view:", calendarView);
    console.log("🔗 Provider connected:", currentProvider?.isConnected);

    if (currentProvider?.isConnected && calendarView === "week") {
      console.log("✅ Conditions met, scheduling scroll...");
      // Longer delay to ensure calendar is fully rendered
      const timer = setTimeout(() => {
        console.log("⏰ Timer fired, calling scrollToCurrentTime");
        scrollToCurrentTime();
      }, 1000);
      return () => {
        console.log("🧹 Cleaning up timer");
        clearTimeout(timer);
      };
    }
  }, [currentProvider?.isConnected, calendarView]);

  const handleSaveEvent = (savedEvent: CalendarEvent) => {
    // Clear creating state
    setIsCreating(false);

    console.log("🔄 handleSaveEvent:", savedEvent);
    console.log("🔄 handleSaveEvent:", convertToCalendarEventWithUI(savedEvent));

    // Update the events list
    setEvents((prevEvents) => {
      const existingIndex = prevEvents.findIndex((e) => e.id === savedEvent.id);
      if (existingIndex >= 0) {
        // Update existing event
        const updatedEvents = [...prevEvents];
        updatedEvents[existingIndex] = convertToCalendarEventWithUI(savedEvent);
        return updatedEvents;
      } else {
        // Add new event
        const newEvent = convertToCalendarEventWithUI(savedEvent);
        return [...prevEvents, newEvent];
      }
    });
  };

  const handleDeleteEvent = (eventId: string) => {
    // Remove the event from the list
    setEvents((prevEvents) => prevEvents.filter((e) => e.id !== eventId));
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setEditingEvent(null);
    setSelectedSlot(null);
    setIsCreating(false); // Reset creating state when modal is closed
  };

  // Custom event component
  const EventComponent = ({ event }: { event: BigCalendarEvent }) => {
    const isRecurring = (event as any).isRecurring;
    const isInstance = (event as any).isInstance;
    const isException = (event as any).isException;
    const isInvited = (event as any).isInvited;
    const userResponseStatus = (event as any).userResponseStatus;
    const isExternal = (event as any).external;

    // Get response status icon and color
    const getResponseIcon = () => {
      switch (userResponseStatus) {
        case "accepted":
          return "✓";
        case "declined":
          return "✗";
        case "tentative":
          return "?";
        default:
          return "📧";
      }
    };

    const getResponseColor = () => {
      switch (userResponseStatus) {
        case "accepted":
          return "#4caf50";
        case "declined":
          return "#f44336";
        case "tentative":
          return "#ff9800";
        default:
          return "#9e9e9e";
      }
    };

    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "2px 4px",
          backgroundColor: event.color,
          color: "white",
          borderRadius: "4px",
          fontSize: "12px",
          border: event.isBlocked ? "2px solid #000" : "none",
          borderLeft: isInvited ? `4px solid ${getResponseColor()}` : "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          {isRecurring && <RepeatIcon sx={{ fontSize: 10, color: "inherit", opacity: 0.8 }} />}
          {isExternal && isInvited && <PersonAddIcon sx={{ fontSize: 10, color: "inherit", opacity: 0.8 }} />}
          {isInvited && <Typography sx={{ fontSize: 10, color: getResponseColor() }}>{getResponseIcon()}</Typography>}
          <Typography
            variant="caption"
            sx={{
              fontWeight: "bold",
              lineHeight: 1,
              textDecoration: isException ? "line-through" : "none",
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {event.title}
          </Typography>
        </Box>
        {event.location && (
          <Typography variant="caption" sx={{ opacity: 0.8, lineHeight: 1 }}>
            📍 {event.location}
          </Typography>
        )}
      </Box>
    );
  };

  // Custom toolbar component
  const CustomToolbar = (toolbar: any) => {
    const goToToday = () => {
      toolbar.onNavigate("TODAY");
    };

    const goToPrev = () => {
      toolbar.onNavigate("PREV");
    };

    const goToNext = () => {
      toolbar.onNavigate("NEXT");
    };

    const goToView = (newView: View) => {
      toolbar.onView(newView);
      setCalendarView(newView);
    };

    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, p: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button onClick={goToToday} variant="outlined" size="small">
            Today
          </Button>
          <IconButton onClick={goToPrev}>
            <ChevronLeftIcon />
          </IconButton>
          <IconButton onClick={goToNext}>
            <ChevronRightIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 2 }}>
            {toolbar.label}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }} data-tutorial="event-actions">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mr: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: "fit-content" }}>
              Timezone:
            </Typography>
            <TimezoneSelector value={calendarDisplayTimezone} onChange={setCalendarDisplayTimezone} autoDetect={false} label="" fullWidth={false} />
          </Box>

          <ToggleButtonGroup value={calendarView} exclusive onChange={(_, newView) => newView && goToView(newView)} size="small">
            <ToggleButton value="month">
              <ViewModuleIcon />
            </ToggleButton>
            <ToggleButton value="week">
              <ViewWeekIcon />
            </ToggleButton>
            <ToggleButton value="day">
              <ViewDayIcon />
            </ToggleButton>
          </ToggleButtonGroup>

          <IconButton onClick={() => fetchEvents(new Date())} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setEditModalOpen(true)} disabled={!currentProvider?.isConnected}>
            Add Event
          </Button>
        </Box>
      </Box>
    );
  };

  const convertToCalendarEventWithUI = (event: CalendarEvent): CalendarEventWithUI => {
    console.log("🔄 Converting CalendarEvent to CalendarEventWithUI:", {
      id: event.id,
      title: event.title,
      startTime: event.startTime,
      endTime: event.endTime,
      allDay: event.allDay,
      timezone: event.timezone,
      eventKeys: Object.keys(event),
    });

    // Use classification if provided (by agent), fallback color mapping
    const classification = (event as any).classification as string | undefined;
    const color = getClassificationColor(classification);
    let startDate: Date;
    let endDate: Date;

    if (event.startTime instanceof Date) {
      startDate = event.startTime;
    } else if (typeof event.startTime === "string") {
      startDate = new Date((event.startTime as string).replace("Z", ""));
    } else {
      startDate = new Date((event.startTime as string).replace("Z", ""));
    }

    // Convert endTime to valid Date
    if (event.endTime instanceof Date) {
      endDate = event.endTime;
    } else if (typeof event.endTime === "string") {
      endDate = new Date((event.endTime as string).replace("Z", ""));
    } else {
      endDate = new Date((event.endTime as string).replace("Z", ""));
    }

    const isRecurring = !!(event as any).recurrence || !!(event as any).baseEventId;
    const isInstance = !!(event as any).baseEventId;
    const isException = !!(event as any).isException;

    const convertedEvent = {
      ...event,
      startTime: startDate,
      endTime: endDate,
      userId: (event as any).userId || "",
      startTimeUTC: (event as any).startTimeUTC || startDate,
      endTimeUTC: (event as any).endTimeUTC || endDate,
      classification,
      color,
      isBlocked: classification === "blocked",
      isRecurring,
      isInstance,
      baseEventId: (event as any).baseEventId,
      isException,
    };

    console.log("🔄 Converted CalendarEventWithUI result:", {
      id: convertedEvent.id,
      title: convertedEvent.title,
      startTime: convertedEvent.startTime,
      endTime: convertedEvent.endTime,
      allDay: convertedEvent.allDay,
      hasStartTime: !!convertedEvent.startTime,
      hasEndTime: !!convertedEvent.endTime,
      startTimeType: typeof convertedEvent.startTime,
      endTimeType: typeof convertedEvent.endTime,
      startTimeValid: convertedEvent.startTime instanceof Date && !isNaN(convertedEvent.startTime.getTime()),
      endTimeValid: convertedEvent.endTime instanceof Date && !isNaN(convertedEvent.endTime.getTime()),
    });

    return convertedEvent;
  };

  // Fetch calendar events for the current view
  const fetchEvents = async (date: Date = new Date()) => {
    try {
      setLoading(true);
      setError(null);

      // Fetch events for a limited range: 1 month back, 6 months forward
      const startDate = new Date(date);
      startDate.setMonth(startDate.getMonth() - 1); // 1 month ago

      const endDate = new Date(date);
      endDate.setMonth(endDate.getMonth() + 6); // 6 months from now

      const calendarEvents = await calendarService.getEvents({
        timeRange: { start: startDate, end: endDate },
        limit: 200,
      });

      console.log(`📅 CalendarPage: Fetched ${calendarEvents.length} events from ${startDate.toISOString()} to ${endDate.toISOString()}`);

      // Log some details about the events
      if (calendarEvents.length > 0) {
        const pastEvents = calendarEvents.filter((e) => e.startTime < new Date());
        const currentEvents = calendarEvents.filter((e) => e.startTime >= new Date());
        const recurringEvents = calendarEvents.filter((e) => (e as any).recurrence || (e as any).baseEventId);
        const instances = calendarEvents.filter((e) => (e as any).baseEventId);
        const exceptions = calendarEvents.filter((e) => (e as any).isException);

        console.log(`📅 CalendarPage: Events breakdown - ${pastEvents.length} past, ${currentEvents.length} current/future`);
        console.log(`📅 CalendarPage: Recurring events - ${recurringEvents.length} total, ${instances.length} instances, ${exceptions.length} exceptions`);

        if (pastEvents.length > 0) {
          console.log(
            `📅 CalendarPage: Past events: ${pastEvents
              .slice(0, 3)
              .map((e) => `${e.title} (${e.startTime.toISOString()})`)
              .join(", ")}${pastEvents.length > 3 ? "..." : ""}`
          );
        }
      }

      // If no events found and we have a connected provider, try to sync first
      if (calendarEvents.length === 0 && currentProvider?.isConnected) {
        console.log("CalendarService: No events found, triggering automatic sync...");
        try {
          await calendarService.syncFromProvider();
          // Fetch events again after sync
          const syncedEvents = await calendarService.getEvents({
            timeRange: { start: startDate, end: endDate },
            limit: 200,
          });
          if (syncedEvents.length > 0) {
            console.log(`CalendarService: Found ${syncedEvents.length} events after sync`);
            calendarEvents.push(...syncedEvents);
          }
        } catch (syncError) {
          console.warn("CalendarService: Automatic sync failed:", syncError);
          // Continue with empty events list
        }
      }

      // Transform calendar events to match UI structure
      const transformedEvents: CalendarEventWithUI[] = calendarEvents.map((event) => {
        return convertToCalendarEventWithUI(event);
      });

      setEvents(transformedEvents);

      // Scroll to current time after events are loaded
      if (calendarView === "week") {
        console.log("📅 Events loaded, scheduling scroll...");
        setTimeout(() => {
          console.log("⏰ Events loaded timer fired, calling scrollToCurrentTime");
          scrollToCurrentTime();
        }, 500);
      }
    } catch (error: any) {
      console.error("Failed to fetch events:", error);
      setError(error.message || "Failed to fetch events");
    } finally {
      setLoading(false);
    }
  };

  // Load data when component mounts
  useEffect(() => {
    const loadCurrentProvider = async () => {
      try {
        const current = await calendarService.getCurrentProvider();
        setCurrentProvider(current);
      } catch (error) {
        console.error("Failed to load current provider:", error);
      }
    };
    loadCurrentProvider();
  }, []);

  // Load events when component mounts or provider changes
  useEffect(() => {
    if (currentProvider?.isConnected) {
      fetchEvents();
    }
  }, [currentProvider]);

  // Force calendar re-render when timezone changes
  useEffect(() => {
    if (calendarDisplayTimezone && events.length > 0) {
      // Force re-render by updating a dummy state or just let the component re-render
      // The convertToBigCalendarEvents function will handle the timezone conversion
      console.log("🔄 Timezone changed to", calendarDisplayTimezone, "- events will be re-converted");
    }
  }, [calendarDisplayTimezone]);

  // Scroll to current time when timezone changes
  useEffect(() => {
    if (calendarDisplayTimezone && calendarView === "week") {
      // Small delay to ensure calendar is rendered
      const timer = setTimeout(() => {
        scrollToCurrentTime();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [calendarDisplayTimezone, calendarView]);

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: "auto" }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading state while checking OAuth */}
      {isGoogleHealthy === null && (
        <Paper sx={{ p: 3, mb: 3, textAlign: "center" }}>
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="h6">Checking Google Calendar connection...</Typography>
        </Paper>
      )}

      {/* React Big Calendar */}
      {isGoogleHealthy === true ? (
        <Paper sx={{ p: 3, mb: 3 }} data-tutorial="calendar-view">
          <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
            <CalendarIcon sx={{ mr: 2, fontSize: 32, color: "primary.main" }} />
            <Typography variant="h4" component="h1">
              Calendar
            </Typography>
            <Box sx={{ ml: "auto" }}>
              <HelpIcon
                tutorialConfig={tutorials.calendar}
                pageDescription="Manage your schedule, create events, and sync with Google Calendar. View your calendar in different formats and drag events to reschedule them!"
              />
            </Box>
          </Box>
          <Box sx={{ height: 600 }}>
            <Box onKeyDown={handleKeyDown} tabIndex={0} sx={{ height: "100%", outline: "none" }}>
              <DnDCalendar
                key={`calendar-${calendarDisplayTimezone || "local"}`}
                ref={calendarRef}
                localizer={localizer}
                events={convertToBigCalendarEvents(events)}
                startAccessor="start"
                endAccessor="end"
                style={{ height: "100%" }}
                view={calendarView}
                onView={setCalendarView}
                onNavigate={handleNavigate}
                onSelectEvent={handleSelectEvent}
                onSelectSlot={handleSelectSlot}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventResize}
                formats={weekFormats}
                draggableAccessor={(event) => (event as any).editable !== false}
                resizableAccessor={(event) => (event as any).editable !== false}
                selectable
                components={{
                  event: EventComponent,
                  toolbar: CustomToolbar,
                }}
                eventPropGetter={(event: BigCalendarEvent) => ({
                  style: {
                    backgroundColor: event.color,
                    border: event.isBlocked ? "2px solid #000" : "none",
                  },
                })}
                messages={{
                  next: "Next",
                  previous: "Previous",
                  today: "Today",
                  month: "Month",
                  week: "Week",
                  day: "Day",
                  agenda: "Agenda",
                  date: "Date",
                  time: "Time",
                  event: "Event",
                  noEventsInRange: "No events in this range.",
                }}
              />
            </Box>

            {/* Loading overlays for different operations */}
            {isMoving && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Moving event...
                  </Typography>
                </Box>
              </Box>
            )}

            {isSaving && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Saving changes...
                  </Typography>
                </Box>
              </Box>
            )}

            {isCreating && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.8)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 1000,
                }}
              >
                <Box sx={{ textAlign: "center" }}>
                  <CircularProgress size={40} />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Creating event...
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Paper>
      ) : (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <CalendarIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            Google Calendar Connection Required
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Please connect your Google Calendar to view and manage your events.
          </Typography>
          <Button variant="contained" onClick={() => (window.location.href = "/google-services")} startIcon={<SettingsIcon />}>
            Connect Google Calendar
          </Button>
        </Paper>
      )}

      {/* Event Edit Modal */}
      <EventEditModal
        open={editModalOpen}
        onClose={handleCloseEditModal}
        event={editingEvent}
        onSave={handleSaveEvent}
        onDelete={(eventId: string) => {
          const event = events.find((e) => e.id === eventId);
          if (event) {
            handleDeleteEventFromCalendar(event);
          }
        }}
        displayTimezone={calendarDisplayTimezone}
        selectedSlot={selectedSlot}
        onScopeSelection={handleEditScopeSelection}
      />

      {/* Recurring Event Scope Modal */}
      <RecurringEventScopeModal
        open={scopeModalOpen}
        onClose={() => {
          setScopeModalOpen(false);
          setPendingAction(null);
        }}
        onConfirm={handleRecurringEventScope}
        eventTitle={pendingAction?.event.title || ""}
        action={pendingAction?.type || "move"}
        loading={isMoving || isSaving}
      />

      {/* Event Details Popup */}
      {selectedEventForDetails && (
        <EventDetailsPopup
          event={convertToTypesCalendarEvent(selectedEventForDetails)}
          onEventUpdate={handleEventDetailsUpdate}
          onEventDelete={handleEventDetailsDelete}
          onEditEvent={handleEventDetailsEdit}
          onClose={handleCloseEventDetails}
        />
      )}
    </Box>
  );
};
